import React, { useEffect, useMemo, useState } from 'react';
import { TableDataView, HeaderItemType } from 'vl-common/src/components/Table/TableDataView';
import { Api } from 'vl-common/src/api';
import { AuditDetailsParams } from 'vl-common/src/api/api.audit';
import {
  AuditedDetailsCategory,
  defaultEndDate,
  defaultStartDate,
  getAuditDetailColumns,
  isOutOfAllowedPeriod,
  transformAuditDetails
} from './auditData';

const AuditDetailsApis: { [key in AuditedDetailsCategory]: (params: AuditDetailsParams) => any } = {
  cases: Api.audit.getCasesAuditDetails,
  appointments: Api.audit.getAppointmentsAuditDetails,
  tasks: Api.audit.getTasksAuditDetails,
  outcomes: Api.audit.getAppointmentsOutcomesDetails,
  user: Api.audit.getUserAuditDetails
};

type NormalisedAuditDetails = {
  [key: string]: number | string | boolean | Date;
};

function normaliseAuditDetail(rawAuditData: any): NormalisedAuditDetails[] {
  return transformAuditDetails(rawAuditData.audit_data);
}

function isSimilar(val1: any, val2: any) {
  if (typeof val1 === 'object') {
    return val1 && val2 && val1.toString() === val2.toString();
  }
  return val1 === val2;
}

// This function is not immutable!
function markDiffs(data: any) {
  let i = 0;
  while (i < data.length) {
    Object.keys(data[i]).forEach((key) => {
      data[i][key] = { value: data[i][key], isAmended: false };
      if (i > 0 && key !== 'Amended Date' && !isSimilar(data[i][key].value, data[i - 1][key].value)) {
        data[i - 1][key].isAmended = true;
      }
    });
    i++;
  }

  return data;
}

interface AuditDetailProps {
  id?: number;
  category?: AuditedDetailsCategory;
  start_date?: any;
  end_date?: any;
  showDiffs?: boolean;
}

async function getDetailedAudit(
  category: AuditedDetailsCategory,
  id: number,
  start_date = defaultStartDate,
  end_date = defaultEndDate
) {
  const res = await AuditDetailsApis[category]({ id, start_date, end_date });
  return normaliseAuditDetail(res.data).reverse();
}

const AuditDetail = ({ id, category, start_date, end_date, showDiffs = true }: AuditDetailProps) => {
  const [state, setState] = useState([]);

  const columns = useMemo(() => {
    const item = state[0];
    if (!item) {
      return [];
    }

    return getAuditDetailColumns(item);
  }, [state]);

  useEffect(() => {
    if (isOutOfAllowedPeriod(start_date, end_date)) {
      setState([]);
    } else if (id && category) {
      getDetailedAudit(category, id, start_date, end_date).then((res) => {
        setState(showDiffs ? markDiffs(res) : res);
      });
    }
  }, [id, category, start_date, end_date]);

  return (
    <TableDataView
      handleTableChange={undefined}
      keyValue={undefined}
      headerItems={columns as HeaderItemType[]}
      dataSource={state}
      scroll={{ y: '60vh', x: '160vw' }}
      // @ts-ignore // To big to be fixed now
      columnTransformer={(column: any, record: any) =>
        (
          <div className={record[column.dataSourceKey]?.isAmended ? 'amended-value' : ''}>
            {column.columnDataFormat ? (
              <column.columnDataFormat data={record[column.dataSourceKey]?.value} entireData={record} />
            ) : (
              record[column.dataSourceKey]?.value
            )}
          </div>
        ) as any
      }
    />
  );
};

export default AuditDetail;
